/**
 * ambientTint.ts
 *
 * The building's own colour, read off the lamps that are on right now.
 *
 * The glass UI can tint itself from the RGB lamps (the default): every
 * switched-on colour-capable light contributes its current colour, weighted by
 * how bright it is and biased towards saturated colours, and the result is one
 * average — "what the house looks like from outside tonight".
 *
 * The mix is a pure function of the entities store, so it follows every
 * `device:value` push with no extra requests and nothing to poll.
 */

import { defineStore } from 'pinia';
import { computed, ref, watch, type ComputedRef } from 'vue';
import { useEntitiesStore } from './entities';
import { isLightDevice } from './lights';
import type { Device } from './entities';

export interface AmbientTint {
  /** `#rrggbb`, for swatches and titles. */
  hex: string;
  /** `r g b`, the space-separated triplet CSS `rgb(var(--ui-tint) / a)` wants. */
  rgb: string;
  /** How many lit colour lamps the mix was taken from. */
  count: number;
}

/** The device values a lamp's live colour and brightness are read from. */
interface ValueLike {
  valueKey?: string;
  currentValue?: unknown;
}

function valuesOf(device: Device): ValueLike[] {
  const values = device.deviceValues;
  return Array.isArray(values) ? (values as ValueLike[]) : [];
}

function findValue(values: ValueLike[], keys: string[]): ValueLike | undefined {
  return values.find((value) => keys.includes(String(value?.valueKey ?? '').toLowerCase()));
}

function isOn(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'on' || value === 'true';
}

/**
 * A live colour as `[r, g, b]` — the same three shapes the room signals read:
 * `#rrggbb`, `"r,g,b"`, or an `[r, g, b]` array.
 */
export function parseRgbValue(value: unknown): [number, number, number] | null {
  if (!value) return null;
  if (typeof value === 'string' && value.startsWith('#')) {
    const hex = value.slice(1);
    if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }

  let parts: number[];
  if (typeof value === 'string') parts = value.split(',').map(Number);
  else if (Array.isArray(value)) parts = value.map(Number);
  else return null;

  const [red, green, blue] = parts;
  if ([red, green, blue].some((part) => !Number.isFinite(part))) return null;
  const clamp = (part: number): number => Math.min(255, Math.max(0, part));
  return [clamp(red), clamp(green), clamp(blue)];
}

/** A lamp's brightness as 0–1 — the hub sends 0–1, 0–100 or 0–255. */
function brightnessWeight(value: unknown): number {
  const level = Number(value);
  if (!Number.isFinite(level)) return 1;
  if (level <= 1) return Math.max(0.15, level);
  if (level <= 100) return Math.max(0.15, level / 100);
  return Math.max(0.15, Math.min(1, level / 255));
}

/** How colourful a colour is, 0–1 (HSV saturation) — grey lamps must not wash the mix out. */
function saturationOf([red, green, blue]: [number, number, number]): number {
  const max = Math.max(red, green, blue);
  if (max === 0) return 0;
  return (max - Math.min(red, green, blue)) / max;
}

/**
 * The current colour mix of every lit colour lamp, or `null` when none is lit
 * (or none reports a colour) — callers show the neutral tint then.
 */
export function mixLitLampColors(devices: Device[]): AmbientTint | null {
  let red = 0;
  let green = 0;
  let blue = 0;
  let weightSum = 0;
  let count = 0;

  for (const device of devices) {
    if (!isLightDevice(device)) continue;
    const values = valuesOf(device);
    if (values.length === 0) continue;
    if (!isOn(findValue(values, ['power', 'on_off', 'state'])?.currentValue)) continue;

    const rgb = parseRgbValue(findValue(values, ['color', 'rgb'])?.currentValue);
    if (!rgb) continue;

    const weight = brightnessWeight(findValue(values, ['brightness', 'dimming', 'level'])?.currentValue);
    // Bias towards colourful lamps: a white lamp should warm the mix, not own it.
    const weightWithSaturation = weight * (0.3 + 0.7 * saturationOf(rgb));

    red += rgb[0] * weightWithSaturation;
    green += rgb[1] * weightWithSaturation;
    blue += rgb[2] * weightWithSaturation;
    weightSum += weightWithSaturation;
    count += 1;
  }

  if (weightSum === 0 || count === 0) return null;

  const channel = (sum: number): number => Math.round(sum / weightSum);
  const [r, g, b] = [channel(red), channel(green), channel(blue)];
  const hex = `#${[r, g, b].map((part) => part.toString(16).padStart(2, '0')).join('')}`;
  return { hex, rgb: `${r} ${g} ${b}`, count };
}

export const useAmbientTintStore = defineStore('ambientTint', () => {
  const entities = useEntitiesStore();

  /**
   * The room whose lamps tint the interface, or `null` for the whole building.
   * A room page sets this while it is open, so walking into the magenta
   * bedroom turns the interface magenta too.
   */
  const scope = ref<string | null>(null);

  /** The live building mix; `null` when no colour lamp is lit right now. */
  const mix: ComputedRef<AmbientTint | null> = computed(() => mixLitLampColors(entities.devices));

  /** The scope room's own mix, or `null` when no room is in scope / nothing is lit there. */
  const roomMix: ComputedRef<AmbientTint | null> = computed(() =>
    scope.value ? mixLitLampColors(entities.devices.filter((device) => device.roomId === scope.value)) : null,
  );

  /** What the interface wears: the room in view first, the building otherwise. */
  const effective: ComputedRef<AmbientTint | null> = computed(() => roomMix.value ?? mix.value);

  /**
   * The last non-null mix. UI tints hold this while the lamps are off so the
   * whole interface does not flash back to grey every time the lights are
   * switched, and so a tinted surface never sits on top of an untinted one
   * mid-transition.
   */
  const lastMix = ref<AmbientTint | null>(null);
  watch(
    effective,
    (value) => {
      if (value) lastMix.value = value;
    },
    { immediate: true },
  );

  function setScope(roomId: string | null): void {
    scope.value = roomId || null;
  }

  /** Teardown guard: a room leaving the screen must not clear the next one's scope. */
  function clearScope(roomId: string | null): void {
    if (scope.value === (roomId || null)) scope.value = null;
  }

  return { scope, mix, roomMix, effective, lastMix, setScope, clearScope };
});
